import type { Field } from '@formily/core';
import { useField } from '@formily/react';
import { observer } from '@formily/reactive-react';
import { useMemo } from 'react';

import type { IProps as IUploadFileProps } from '../../../components/UploadFile';
import { IFormFieldSchema } from '../../types';
import ChunkUploadFile, {
  ChunkFileUploadMode,
} from '../../../components/ChunkFileUpload';
import { IFile } from '../../../types/file';
import { API_INTERACT_URL } from '../../../utils/urls';

export interface IFormFilesUploadProps extends IUploadFileProps {
  visible?: boolean;
  label?: string;
  placeholder?: string;
  chunkSize?: number;
}

export interface IProps {
  schema?: IFormFieldSchema<IFormFilesUploadProps>;
}

const fileUploadedTransformer = (file: IFile) => {
  return {
    id: file.id,
    disk: file.disk,
    bucket: file.bucket,
    path: file.path,
    name: file.name,
    size: file.size,
    extension: file.extension,
  };
};

const FormChunkUpload = observer(({ schema }: IProps = {}) => {
  const field = useField<Field>();

  const componentProps = schema
    ? ((schema['x-component-props'] ?? {}) as IFormFilesUploadProps)
    : ((field.componentProps ?? {}) as IFormFilesUploadProps);

  const { visible, accept, placeholder, chunkSize } = componentProps;

  const handleUploadSuccess = (uploadedFile: IFile) => {
    field.setValue(fileUploadedTransformer(uploadedFile));
  };

  const handleDeleteFile = () => {
    field.setValue(null);
  };

  const acceptFiles = Array.isArray(accept) ? accept.join(',') : accept;

  const mode = useMemo(() => {
    if (field?.disabled) return ChunkFileUploadMode.View;
    return ChunkFileUploadMode.Upload;
  }, [field?.disabled]);

  if (visible === false) return null;

  if (!field) {
    // UI Builder mode
    return (
      <ChunkUploadFile
        initializeUploadURL={`${API_INTERACT_URL}/files/multipart/start`}
        finalizeUploadURL={`${API_INTERACT_URL}/files/multipart/complete`}
        mode={mode}
        onComplete={handleUploadSuccess}
        onDelete={handleDeleteFile}
        placeholder={placeholder}
        chunkSize={chunkSize}
        label={schema?.title}
        required={schema?.required}
      />
    );
  }

  return (
    <ChunkUploadFile
      initializeUploadURL={`${API_INTERACT_URL}/files/multipart/start`}
      finalizeUploadURL={`${API_INTERACT_URL}/files/multipart/complete`}
      mode={mode}
      onComplete={handleUploadSuccess}
      onDelete={handleDeleteFile}
      initFile={field?.value as IFile}
      accept={acceptFiles}
      placeholder={placeholder}
      chunkSize={chunkSize}
    />
  );
});

export default FormChunkUpload;
